var debounce = require('./debounce');
var anchorPointUtilities = require('./anchorPointUtilities');
var reconnectionUtilities = require('./reconnectionUtilities');
var registerUndoRedoFunctions = require('./registerUndoRedoFunctions');
var edgeHighlight = require('./edgeHighlight');
var resolveEdgeToHighlight = edgeHighlight.resolveEdgeToHighlight;
var highlightTargetOf = edgeHighlight.highlightTargetOf;
var stageId = 0;

module.exports = function(params, cy) {
  var fn = params;

  anchorPointUtilities.options = params;

  var addBendPointCxtMenuId = 'cy-edge-bend-editing-cxt-add-bend-point' + stageId;
  var removeBendPointCxtMenuId = 'cy-edge-bend-editing-cxt-remove-bend-point' + stageId;
  var removeAllBendPointCtxMenuId = 'cy-edge-bend-editing-cxt-remove-multiple-bend-point' + stageId;
  var addControlPointCxtMenuId = 'cy-edge-control-editing-cxt-add-control-point' + stageId;
  var removeControlPointCxtMenuId = 'cy-edge-control-editing-cxt-remove-control-point' + stageId;
  var removeAllControlPointCtxMenuId = 'cy-edge-bend-editing-cxt-remove-multiple-control-point' + stageId;
  var eStyle, eRemove, eAdd, eZoom, eSelect, eUnselect, eTapStart, eTapStartOnEdge, eTapDrag, eTapEnd, eCxtTap, eDrag,
    eData;
  // last status of gestures
  var lastPanningEnabled, lastZoomingEnabled, lastBoxSelectionEnabled;
  var lastActiveBgOpacity;
  // status of edge to highlight bends and selected edges
  var edgeToHighlight;

  // the Kanva.shape() for the endpoints
  var endpointShape1 = null, endpointShape2 = null;
  // used to stop certain cy listeners when interracting with anchors
  var anchorTouched = false;
  // used call eMouseDown of anchorManager if the mouse is out of the content on cy.on(tapend)
  var mouseOut;

  var functions = {
    init: function() {
      // register undo redo functions
      registerUndoRedoFunctions(cy, anchorPointUtilities, params);
      document.addEventListener('addAnchorEvent', function(event) {
        const eventWithTarget = { target: event.detail.link };
        const type = event.detail.anchorType;
        const anchorPoints = event.detail.anchorPoints;
        // Capture the edge reference to avoid race conditions when processing multiple links
        const capturedEdge = eventWithTarget.target;
        anchorPointUtilities.currentCtxEdge = capturedEdge;
        anchorPointUtilities.currentCtxPos = anchorPoints.source;
        cxtAddAnchorFcn(eventWithTarget, type);
        setTimeout(() => {
          // Restore the captured edge reference before adding second anchor point
          // This prevents the race condition where another link's event overwrites currentCtxEdge
          anchorPointUtilities.currentCtxEdge = capturedEdge;
          anchorPointUtilities.currentCtxPos = anchorPoints.target;
          cxtAddAnchorFcn(eventWithTarget, type);
        }, 300);
      });
      var opts = params;

      /*
        Make sure we don't append an element that already exists.
        This extension canvas uses the same html element as edge-editing.
        It makes sense since it also uses the same Konva stage.
        Without the below logic, an empty canvasElement would be created
        for one of these extensions for no reason.
      */
      var $container = cy.container();
      var containerMetaData = null;
      var canvasElementId = 'cy-node-edge-editing-stage' + stageId;
      stageId++;
      var $canvasElement = document.createElement('div');
      $canvasElement.setAttribute('id', canvasElementId);

      if (!$container.querySelector('#' + canvasElementId)) {
        $container.append($canvasElement);
      }

      /*
        Maintain a single Konva.stage object throughout the application that uses this extension
        such as Newt. This is important since having different stages causes weird behavior
        on other extensions that also use Konva, like not listening to mouse clicks and such.
        If you are someone that is creating an extension that uses Konva in the future, you need to
        be careful about how events register. If you use a different stage almost certainly one
        or both of the extensions that use the stage created below will break.
      */
      var stage;
      if (Konva.stages.length < stageId) {
        stage = new Konva.Stage({
          id: 'node-edge-editing-stage',
          container: canvasElementId,   // id of container <div>
          width: $container.clientWidth,
          height: $container.clientHeight
        });
      } else {
        stage = Konva.stages[stageId - 1];
      }

      var canvas;
      if (stage.getChildren().length < 1) {
        canvas = new Konva.Layer();
        stage.add(canvas);
      } else {
        canvas = stage.getChildren()[0];
      }

      var anchorManager = {
        edge: undefined,
        edgeType: 'none',
        anchors: [],
        // remembers the touched anchor to avoid clearing it when dragging happens
        touchedAnchor: undefined,
        // remembers the index of the moving anchor
        touchedAnchorIndex: undefined,
        bindListeners: function(anchor) {
          anchor.on('mousedown touchstart', this.eMouseDown);
        },
        unbindListeners: function(anchor) {
          anchor.off('mousedown touchstart', this.eMouseDown);
        },
        // gets trigger on clicking on context menus, while cy listeners don't get triggered
        // it can cause weird behaviour if not aware of this
        eMouseDown: function(event) {
          // anchorManager.edge.unselect() won't work sometimes if this wasn't here
          cy.autounselectify(false);

          // eMouseDown(set) -> tapdrag(used) -> eMouseUp(reset)
          anchorTouched = true;
          anchorManager.touchedAnchor = event.target;
          mouseOut = false;
          anchorManager.edge.unselect();

          // Emit custom event for label hiding during drag operations
          cy.emit('edgeediting_dragstart');

          // remember state before changing
          var weightStr = anchorPointUtilities.syntax[anchorManager.edgeType]['weight'];
          var distanceStr = anchorPointUtilities.syntax[anchorManager.edgeType]['distance'];

          var edge = anchorManager.edge;
          moveAnchorParam = {
            edge: edge,
            type: anchorManager.edgeType,
            weights: edge.data(weightStr) ? [].concat(edge.data(weightStr)) : [],
            distances: edge.data(distanceStr) ? [].concat(edge.data(distanceStr)) : []
          };

          // Capture full edge state for undo/redo
          anchorStateBefore = {
            edgeId: edge.id(),
            bendPointPositions: edge.data('bendPointPositions') ? JSON.parse(JSON.stringify(edge.data('bendPointPositions'))) : null,
            controlPointPositions: edge.data('controlPointPositions') ? JSON.parse(JSON.stringify(edge.data('controlPointPositions'))) : null,
            cyedgebendeditingDistances: edge.data('cyedgebendeditingDistances') ? [].concat(edge.data('cyedgebendeditingDistances')) : null,
            cyedgebendeditingWeights: edge.data('cyedgebendeditingWeights') ? [].concat(edge.data('cyedgebendeditingWeights')) : null,
            cyedgecontroleditingDistances: edge.data('cyedgecontroleditingDistances') ? [].concat(edge.data('cyedgecontroleditingDistances')) : null,
            cyedgecontroleditingWeights: edge.data('cyedgecontroleditingWeights') ? [].concat(edge.data('cyedgecontroleditingWeights')) : null
          };

          turnOffActiveBgColor();
          disableGestures();

          cy.autoungrabify(true);

          canvas.getStage().on('contentTouchend contentMouseup', anchorManager.eMouseUp);
          canvas.getStage().on('contentMouseout', anchorManager.eMouseOut);
        },
        // gets called before cy.on('tapend')
        eMouseUp: function(event) {
          // won't be called if the mouse is released out of screen
          anchorTouched = false;
          anchorManager.touchedAnchor = undefined;
          mouseOut = false;
          // Throwing here used to skip the rest of the teardown, leaving the
          // canvas with gestures disabled and the link stuck without handles.
          if (anchorManager.edge && anchorManager.edge.length > 0) {
            anchorManager.edge.select();
          }

          resetActiveBgColor();
          resetGestures();

          /*
           * IMPORTANT
           * Any programmatic calls to .select(), .unselect() after this statement are ignored
           * until cy.autounselectify(false) is called in one of the previous:
           *
           * cy.on('tapstart')
           * anchor.on('mousedown touchstart')
           * document.on('keydown')
           * cy.on('tapdrap')
           *
           * Doesn't affect UX, but may cause confusing behaviour if not aware of this when coding
           *
           * Why is this here?
           * This is important to keep edges from being auto deselected from working
           * with anchors out of the edge body (for unbundled bezier, technically not necessery for segements).
           *
           * These is anther cy.autoselectify(true) in cy.on('tapend')
           *
          */
          cy.autounselectify(true);
          cy.autoungrabify(false);

          canvas.getStage().off('contentTouchend contentMouseup', anchorManager.eMouseUp);
          canvas.getStage().off('contentMouseout', anchorManager.eMouseOut);
        },
        // handle mouse going out of canvas
        eMouseOut: function(event) {
          mouseOut = true;
        },
        clearAnchorsExcept: function(dontClean = undefined) {
          var exceptionApplies = false;

          this.anchors.forEach((anchor, index) => {
            if (dontClean && anchor === dontClean) {
              exceptionApplies = true; // the dontClean anchor is not cleared
              return;
            }

            this.unbindListeners(anchor);
            anchor.destroy();
          });

          if (exceptionApplies) {
            this.anchors = [dontClean];
          } else {
            this.anchors = [];
            // A drag in flight still needs the edge: eMouseUp re-selects it, and
            // the shape it started from may already have been re-rendered away.
            if (!anchorTouched) {
              this.edge = undefined;
              this.edgeType = 'none';
            }
          }
        },
        // render the bend and control shapes of the given edge
        renderAnchorShapes: function(edge) {
          this.edge = edge;
          this.edgeType = anchorPointUtilities.getEdgeType(edge);

          if (edge.hasClass('cant-edit') || !opts.handleAnchors || (!edge.hasClass('edgebendediting-hasbendpoints') &&
            !edge.hasClass('edgecontrolediting-hascontrolpoints'))) {
            return;
          }

          var anchorList = anchorPointUtilities.getAnchorsAsArray(edge);//edge._private.rdata.segpts;
          var length = getAnchorShapesLength(edge) * 0.65;

          for (var i = 0; anchorList && i < anchorList.length; i = i + 2) {
            var anchorX = anchorList[i];
            var anchorY = anchorList[i + 1];

            this.renderAnchorShape(anchorX, anchorY, length);
          }

          canvas.draw();
        },
        // render a anchor shape with the given parameters
        renderAnchorShape: function(anchorX, anchorY, length) {
          // get the top left coordinates
          var topLeftX = anchorX - length / 2;
          var topLeftY = anchorY - length / 2;

          // convert to rendered parameters
          var renderedTopLeftPos = convertToRenderedPosition({ x: topLeftX, y: topLeftY });
          length *= cy.zoom();

          var newAnchor = new Konva.Rect({
            x: renderedTopLeftPos.x,
            y: renderedTopLeftPos.y,
            width: length,
            height: length,
            fill: opts.anchorColor,
            strokeWidth: 0,
            draggable: true
          });

          this.anchors.push(newAnchor);
          this.bindListeners(newAnchor);
          canvas.add(newAnchor);
        }
      };

      var cxtAddBendFcn = function(event) {
        cxtAddAnchorFcn(event, 'bend');
      };

      var cxtAddControlFcn = function(event) {
        cxtAddAnchorFcn(event, 'control');
      };

      var cxtAddAnchorFcn = function(event, anchorType) {
        var edge = event.target || event.cyTarget;
        if (!anchorPointUtilities.isIgnoredEdge(edge)) {

          var type = anchorPointUtilities.getEdgeType(edge);
          var weights, distances, weightStr, distanceStr;

          if (type === 'none') {
            weights = [];
            distances = [];
          } else {
            weightStr = anchorPointUtilities.syntax[type]['weight'];
            distanceStr = anchorPointUtilities.syntax[type]['distance'];

            weights = edge.data(weightStr) ? [].concat(edge.data(weightStr)) : edge.data(weightStr);
            distances = edge.data(distanceStr) ? [].concat(edge.data(distanceStr)) : edge.data(distanceStr);
          }

          var param = {
            edge: edge,
            type: type,
            weights: weights,
            distances: distances
          };
          // the undefined go for edge and newAnchorPoint parameters
          anchorPointUtilities.addAnchorPoint(undefined, undefined, anchorType);

          if (options().undoable) {
            cy.undoRedo().do('changeAnchorPoints', param);
          }
        }

        refreshDraws();

        edge.select();
      };

      var cxtRemoveAnchorFcn = function(event) {
        var edge = anchorManager.edge;
        var type = anchorPointUtilities.getEdgeType(edge);

        if (anchorPointUtilities.edgeTypeNoneShouldntHappen(type, 'UiUtilities.js, cxtRemoveAnchorFcn')) {
          return;
        }

        var param = {
          edge: edge,
          type: type,
          weights: [].concat(edge.data(anchorPointUtilities.syntax[type]['weight'])),
          distances: [].concat(edge.data(anchorPointUtilities.syntax[type]['distance']))
        };

        anchorPointUtilities.removeAnchor();

        if (options().undoable) {
          cy.undoRedo().do('changeAnchorPoints', param);
        }

        setTimeout(function() {
          refreshDraws();
          edge.select();
        }, 50);

      };

      var cxtRemoveAllAnchorsFcn = function(event) {
        var edge = anchorManager.edge;
        var type = anchorPointUtilities.getEdgeType(edge);
        var param = {
          edge: edge,
          type: type,
          weights: [].concat(edge.data(anchorPointUtilities.syntax[type]['weight'])),
          distances: [].concat(edge.data(anchorPointUtilities.syntax[type]['distance']))
        };

        anchorPointUtilities.removeAllAnchors();

        if (options().undoable) {
          cy.undoRedo().do('changeAnchorPoints', param);
        }
        setTimeout(function() {
          refreshDraws();
          edge.select();
        }, 50);
      };

      // function to validate edge source and target on reconnection
      var validateEdge = opts.validateEdge;
      // function to be called on invalid edge reconnection
      var actOnUnsuccessfulReconnection = opts.actOnUnsuccessfulReconnection;

      var menuItems = [];

      if (opts.addBendMenuItemTitle) {
        menuItems.push({
          id: addBendPointCxtMenuId,
          content: opts.addBendMenuItemTitle,
          selector: 'edge',
          onClickFunction: cxtAddBendFcn,
          hasTrailingDivider: opts.useTrailingDividersAfterContextMenuOptions
        });
      }
      if (opts.removeBendMenuItemTitle) {
        menuItems.push({
          id: removeBendPointCxtMenuId,
          content: opts.removeBendMenuItemTitle,
          selector: 'edge',
          onClickFunction: cxtRemoveAnchorFcn,
          hasTrailingDivider: opts.useTrailingDividersAfterContextMenuOptions
        });
      }
      if (opts.addControlMenuItemTitle) {
        menuItems.push({
          id: addControlPointCxtMenuId,
          content: opts.addControlMenuItemTitle,
          selector: 'edge',
          onClickFunction: cxtAddControlFcn,
          hasTrailingDivider: opts.useTrailingDividersAfterContextMenuOptions
        });
      }
      if (opts.removeControlMenuItemTitle) {
        menuItems.push({
          id: removeControlPointCxtMenuId,
          content: opts.removeControlMenuItemTitle,
          selector: 'edge',
          onClickFunction: cxtRemoveAnchorFcn,
          hasTrailingDivider: opts.useTrailingDividersAfterContextMenuOptions
        });
      }
      if (opts.removeAllBendMenuItemTitle) {
        menuItems.push({
          id: removeAllBendPointCtxMenuId,
          content: opts.removeAllBendMenuItemTitle,
          selector: ':selected.edgebendediting-hasmultiplebendpoints',
          onClickFunction: cxtRemoveAllAnchorsFcn,
          hasTrailingDivider: opts.useTrailingDividersAfterContextMenuOptions
        });
      }
      if (opts.removeAllControlMenuItemTitle) {
        menuItems.push({
          id: removeAllControlPointCtxMenuId,
          content: opts.removeAllControlMenuItemTitle,
          selector: ':selected.edgecontrolediting-hasmultiplecontrolpoints',
          onClickFunction: cxtRemoveAllAnchorsFcn,
          hasTrailingDivider: opts.useTrailingDividersAfterContextMenuOptions
        });
      }
      if (!opts.handleAnchors) {
        menuItems = [];
        opts.removeAllControlMenuItemTitle = false;
        opts.removeAllBendMenuItemTitle = false;
        opts.removeControlMenuItemTitle = false;
        opts.addControlMenuItemTitle = false;
        opts.removeBendMenuItemTitle = false;
        opts.addBendMenuItemTitle = false;
      }
      var hasNoMenuItems = menuItems.length === 0;
      if (cy.contextMenus && !hasNoMenuItems) {
        var menus = cy.contextMenus('get');
        opts.contexMenuShowEvent = menus.getOptions().evtType;
        // If context menus is active just append menu items else activate the extension
        // with initial menu items
        if (menus.isActive()) {
          menus.appendMenuItems(menuItems);
        } else {
          cy.contextMenus({
            evtType: opts.contexMenuShowEvent,
            menuItems: menuItems
          });
        }
      }

      var _sizeCanvas = debounce(function() {
        $canvasElement.setAttribute('height', $container.clientHeight);
        $canvasElement.setAttribute('width', $container.clientWidth);
        $canvasElement.style.position = 'absolute';
        $canvasElement.style.top = '0';
        $canvasElement.style.left = '0';
        $canvasElement.style.zIndex = options().zIndex;

        setTimeout(function() {
          var canvasBb = offset($canvasElement);
          var containerBb = offset($container);

          $canvasElement.style.top = '' + -(canvasBb.top - containerBb.top);
          $canvasElement.style.left = '' + -(canvasBb.left - containerBb.left);

          canvas.getStage().setWidth($container.clientWidth);
          canvas.getStage().setHeight($container.clientHeight);

          // redraw on canvas resize
          if (cy) {
            refreshDraws();
          }
        }, 0);

      }, 250);

      function offset(el) {
        var rect = el.getBoundingClientRect();
        return {
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX
        };
      }

      function sizeCanvas() {
        _sizeCanvas();
      }

      sizeCanvas();

      window.addEventListener('resize', function() {
        sizeCanvas();
      });

      // write options to data
      var data = containerMetaData;
      if (data == null) {
        data = {};
      }
      data.options = opts;

      var optCache;

      function options() {
        return optCache || (optCache = containerMetaData.options);
      }

      // we will need to convert model positons to rendered positions
      function convertToRenderedPosition(modelPosition) {
        var pan = cy.pan();
        var zoom = cy.zoom();

        var x = modelPosition.x * zoom + pan.x;
        var y = modelPosition.y * zoom + pan.y;

        return {
          x: x,
          y: y
        };
      }

      function refreshDraws() {

        // don't clear anchor which is being moved
        anchorManager.clearAnchorsExcept(anchorManager.touchedAnchor);

        if (endpointShape1 !== null) {
          endpointShape1.destroy();
          endpointShape1 = null;
        }
        if (endpointShape2 !== null) {
          endpointShape2.destroy();
          endpointShape2 = null;
        }
        canvas.draw();

        if (edgeToHighlight) {
          anchorManager.renderAnchorShapes(edgeToHighlight);
          renderEndPointShapes(edgeToHighlight);
        }
      }

      // External hook so consumers can force anchor/endpoint colors to be
      // re-read from opts (e.g. on appearance change). MAP-1113.
      cy.on('edgeediting_refresh', refreshDraws);

      // render the end points shapes of the given edge
      function renderEndPointShapes(edge) {
        if (!edge || !opts.handleReconnectEdge) {
          return;
        }

        var edge_pts = anchorPointUtilities.getAnchorsAsArray(edge);
        if (typeof edge_pts === 'undefined') {
          edge_pts = [];
        }
        var sourcePos = edge.sourceEndpoint();
        var targetPos = edge.targetEndpoint();

        // This function is called inside refreshDraws which is called
        // for updating Konva shapes on events, but sometimes these values
        // will be NaN and Konva will show warnings in console as a result
        // This is a check to eliminate those cases since if these values
        // are NaN nothing will be drawn anyway.
        if (!sourcePos.x || !targetPos.x) {
          return;
        }

        edge_pts.unshift(sourcePos.y);
        edge_pts.unshift(sourcePos.x);
        edge_pts.push(targetPos.x);
        edge_pts.push(targetPos.y);


        if (!edge_pts)
          return;

        var src = {
          x: edge_pts[0],
          y: edge_pts[1]
        };

        var target = {
          x: edge_pts[edge_pts.length - 2],
          y: edge_pts[edge_pts.length - 1]
        };

        var nextToSource = {
          x: edge_pts[2],
          y: edge_pts[3]
        };
        var nextToTarget = {
          x: edge_pts[edge_pts.length - 4],
          y: edge_pts[edge_pts.length - 3]
        };
        var length = getAnchorShapesLength(edge) * 0.65;

        renderEachEndPointShape(src, target, length, nextToSource, nextToTarget);

      }

      function renderEachEndPointShape(source, target, length, nextToSource, nextToTarget) {
        // get the top left coordinates of source and target
        var sTopLeftX = source.x - length / 2;
        var sTopLeftY = source.y - length / 2;

        var tTopLeftX = target.x - length / 2;
        var tTopLeftY = target.y - length / 2;

        var nextToSourceX = nextToSource.x - length / 2;
        var nextToSourceY = nextToSource.y - length / 2;

        var nextToTargetX = nextToTarget.x - length / 2;
        var nextToTargetY = nextToTarget.y - length / 2;


        // convert to rendered parameters
        var renderedSourcePos = convertToRenderedPosition({ x: sTopLeftX, y: sTopLeftY });
        var renderedTargetPos = convertToRenderedPosition({ x: tTopLeftX, y: tTopLeftY });
        length = length * cy.zoom() / 2;

        var renderedNextToSource = convertToRenderedPosition({ x: nextToSourceX, y: nextToSourceY });
        var renderedNextToTarget = convertToRenderedPosition({ x: nextToTargetX, y: nextToTargetY });

        //how far to go from the node along the edge
        var distanceFromNode = length;

        var distanceSource = Math.sqrt(Math.pow(renderedNextToSource.x - renderedSourcePos.x, 2) + Math.pow(renderedNextToSource.y - renderedSourcePos.y, 2));
        var sourceEndPointX = renderedSourcePos.x + ((distanceFromNode / distanceSource) * (renderedNextToSource.x - renderedSourcePos.x));
        var sourceEndPointY = renderedSourcePos.y + ((distanceFromNode / distanceSource) * (renderedNextToSource.y - renderedSourcePos.y));


        var distanceTarget = Math.sqrt(Math.pow(renderedNextToTarget.x - renderedTargetPos.x, 2) + Math.pow(renderedNextToTarget.y - renderedTargetPos.y, 2));
        var targetEndPointX = renderedTargetPos.x + ((distanceFromNode / distanceTarget) * (renderedNextToTarget.x - renderedTargetPos.x));
        var targetEndPointY = renderedTargetPos.y + ((distanceFromNode / distanceTarget) * (renderedNextToTarget.y - renderedTargetPos.y));

        // render end point shape for source and target
        // the null checks are not theoretically required
        // but they protect from bad synchronious calls of refreshDraws()
        if (endpointShape1 === null) {
          endpointShape1 = new Konva.Circle({
            x: sourceEndPointX + length,
            y: sourceEndPointY + length,
            radius: length,
            fill: opts.endPointColor
          });
        }

        if (endpointShape2 === null) {
          endpointShape2 = new Konva.Circle({
            x: targetEndPointX + length,
            y: targetEndPointY + length,
            radius: length,
            fill: opts.endPointColor
          });
        }

        canvas.add(endpointShape1);
        canvas.add(endpointShape2);
        canvas.draw();

      }

      // get the length of anchor points to be rendered
      function getAnchorShapesLength(edge) {
        var factor = opts.anchorShapeSizeFactor;
        var actualFactor = 0;
        if (opts.enableFixedAnchorSize) {
          actualFactor = factor / cy.zoom();
        } else {
          actualFactor = factor;
        }
        if (parseFloat(edge.css('width')) <= 2.5) {
          return 2.5 * actualFactor;
        }
        return parseFloat(edge.css('width')) * actualFactor;
      }

      // check if the anchor represented by {x, y} is inside the point shape
      function checkIfInsideShape(x, y, length, centerX, centerY) {
        var minX = centerX - length / 2;
        var maxX = centerX + length / 2;
        var minY = centerY - length / 2;
        var maxY = centerY + length / 2;

        var inside = (x >= minX && x <= maxX) && (y >= minY && y <= maxY);
        return inside;
      }

      // get the index of anchor containing the point represented by {x, y}
      function getContainingShapeIndex(x, y, edge) {
        var type = anchorPointUtilities.getEdgeType(edge);

        if (type === 'none') {
          return -1;
        }

        if (edge.data(anchorPointUtilities.syntax[type]['weight']) == null ||
          edge.data(anchorPointUtilities.syntax[type]['weight']).length == 0) {
          return -1;
        }

        var anchorList = anchorPointUtilities.getAnchorsAsArray(edge);//edge._private.rdata.segpts;
        var length = getAnchorShapesLength(edge);

        for (var i = 0; anchorList && i < anchorList.length; i = i + 2) {
          var anchorX = anchorList[i];
          var anchorY = anchorList[i + 1];

          var inside = checkIfInsideShape(x, y, length, anchorX, anchorY);
          if (inside) {
            return i / 2;
          }
        }

        return -1;
      };

      function getContainingEndPoint(x, y, edge) {
        var length = getAnchorShapesLength(edge);
        var allPts = edge._private.rscratch.allpts;
        var src = {
          x: allPts[0],
          y: allPts[1]
        };
        var target = {
          x: allPts[allPts.length - 2],
          y: allPts[allPts.length - 1]
        };
        convertToRenderedPosition(src);
        convertToRenderedPosition(target);

        // Source:0, Target:1, None:-1
        if (checkIfInsideShape(x, y, length, src.x, src.y))
          return 0;
        else if (checkIfInsideShape(x, y, length, target.x, target.y))
          return 1;
        else
          return -1;
      }

      // store the current status of gestures and set them to false
      function disableGestures() {
        lastPanningEnabled = cy.panningEnabled();
        lastZoomingEnabled = cy.zoomingEnabled();
        lastBoxSelectionEnabled = cy.boxSelectionEnabled();

        cy.zoomingEnabled(false)
          .panningEnabled(false)
          .boxSelectionEnabled(false);
      }

      // reset the gestures by their latest status
      function resetGestures() {
        cy.zoomingEnabled(lastZoomingEnabled)
          .panningEnabled(lastPanningEnabled)
          .boxSelectionEnabled(lastBoxSelectionEnabled);
      }

      function turnOffActiveBgColor() {
        // found this at the cy-node-resize code, but doesn't seem to find the object most of the time
        if (cy.style()._private.coreStyle['active-bg-opacity']) {
          lastActiveBgOpacity = cy.style()._private.coreStyle['active-bg-opacity'].value;
        } else {
          // arbitrary, feel free to change
          // trial and error showed that 0.15 was closest to the old color
          lastActiveBgOpacity = 0.15;
        }

        cy.style()
          .selector('core')
          .style('active-bg-opacity', 0)
          .update();
      }

      function resetActiveBgColor() {
        cy.style()
          .selector('core')
          .style('active-bg-opacity', lastActiveBgOpacity)
          .update();
      }

      function moveAnchorPoints(positionDiff, edges) {
        edges.forEach(function(edge) {
          var previousAnchorsPosition = anchorPointUtilities.getAnchorsAsArray(edge);
          var nextAnchorPointsPosition = [];
          if (previousAnchorsPosition != undefined) {
            for (var i = 0; i < previousAnchorsPosition.length; i += 2) {
              nextAnchorPointsPosition.push({
                x: previousAnchorsPosition[i] + positionDiff.x,
                y: previousAnchorsPosition[i + 1] + positionDiff.y
              });
            }
            var type = anchorPointUtilities.getEdgeType(edge);

            if (anchorPointUtilities.edgeTypeNoneShouldntHappen(type, 'UiUtilities.js, moveAnchorPoints')) {
              return;
            }

            if (type === 'bend') {
              params.bendPointPositionsSetterFunction(edge, nextAnchorPointsPosition);
            } else if (type === 'control') {
              params.controlPointPositionsSetterFunction(edge, nextAnchorPointsPosition);
            }
          }
        });
        anchorPointUtilities.initAnchorPoints(options().bendPositionsFunction, options().controlPositionsFunction, edges);

        // Listener defined in other extension
        // Might have compatibility issues after the unbundled bezier
        cy.trigger('bendPointMovement');
      }

      function moveAnchorOnDrag(edge, type, index, position) {
        var weights = edge.data(anchorPointUtilities.syntax[type]['weight']);
        var distances = edge.data(anchorPointUtilities.syntax[type]['distance']);

        var relativeAnchorPosition = anchorPointUtilities.convertToRelativePosition(edge, position);
        weights[index] = relativeAnchorPosition.weight;
        distances[index] = relativeAnchorPosition.distance;

        edge.data(anchorPointUtilities.syntax[type]['weight'], weights);
        edge.data(anchorPointUtilities.syntax[type]['distance'], distances);

        // Emit custom event for HTML labels to update in real-time
        cy.trigger('edgeediting_anchordrag', [edge, type, index, position]);
      }

      // debounced due to large amout of calls to tapdrag
      var _moveAnchorOnDrag = debounce(moveAnchorOnDrag, 5);

      {
        lastPanningEnabled = cy.panningEnabled();
        lastZoomingEnabled = cy.zoomingEnabled();
        lastBoxSelectionEnabled = cy.boxSelectionEnabled();

        function syncHighlightToSelection() {
          cy.startBatch();
          if (edgeToHighlight) {
            edgeToHighlight.removeClass('cy-edge-editing-highlight');
          }
          edgeToHighlight = highlightTargetOf(cy.edges(':selected'));
          if (edgeToHighlight) {
            edgeToHighlight.addClass('cy-edge-editing-highlight');
          }
          cy.endBatch();
        }

        edgeToHighlight = highlightTargetOf(cy.edges(':selected'));

        cy.bind('zoom pan', eZoom = function() {
          if (!edgeToHighlight) {
            return;
          }

          refreshDraws();
        });

        cy.on('data', 'edge', eData = function() {
          const selected = cy.edges(':selected');
          if (!edgeToHighlight && selected.length === 0) {
            return;
          }

          edgeToHighlight = resolveEdgeToHighlight(
            selected,
            window && window.getTappedLink ? (window.getTappedLink() || {}).id : undefined,
            edgeToHighlight
          );

          refreshDraws();
        });

        cy.on('style', 'edge.edgebendediting-hasbendpoints:selected, edge.edgecontrolediting-hascontrolpoints:selected', eStyle = function() {
          setTimeout(function() {
            refreshDraws();
          }, 50);
        });

        cy.on('remove', 'edge', eRemove = function() {
          var edge = this;
          if (edge.selected()) {
            syncHighlightToSelection();
          }
          refreshDraws();
        });

        cy.on('add', 'edge', eAdd = function() {
          var edge = this;
          if (edge.selected()) {
            syncHighlightToSelection();
          }
          refreshDraws();
        });

        eSelect = function(edge) {
          if (edge.target().connectedEdges().length == 0 || edge.source().connectedEdges().length == 0) {
            return;
          }

          syncHighlightToSelection();
          refreshDraws();
        };

        cy.on('select', 'edge', function(event) {
          if (opts.isShowHandleOnHover) {
            return;
          }
          eSelect(event.target);
        });

        eUnselect = function() {
          syncHighlightToSelection();
          refreshDraws();
        };

        cy.on('unselect', 'edge', function() {
          if (opts.isShowHandleOnHover) {
            return;
          }
          eUnselect();
        });

        cy.on('mouseover', 'edge', function(event) {
          if (!opts.isShowHandleOnHover) {
            return;
          }
          eSelect(event.target);
        });

        cy.on('mouseout', 'edge', function() {
          if (!opts.isShowHandleOnHover) {
            return;
          }
          eUnselect();
        });

        var movedAnchorIndex;
        var tapStartPos;
        var movedEdge;
        var moveAnchorParam;
        var anchorStateBefore; // Full edge control point state before manipulation (for undo/redo)
        var createAnchorOnDrag;
        var movedEndPoint;
        var dummyNode;
        var detachedNode;
        var nodeToAttach;
        var anchorCreatedByDrag = false;

        cy.on('tapstart', eTapStart = function(event) {
          tapStartPos = event.position || event.cyPosition;
        });

        cy.on('tapstart', 'edge', eTapStartOnEdge = function(event) {
          var edge = this;

          if (!edgeToHighlight || edgeToHighlight.id() !== edge.id()) {
            createAnchorOnDrag = false;
            return;
          }

          movedEdge = edge;

          var type = anchorPointUtilities.getEdgeType(edge);

          // to avoid errors
          if (type === 'none')
            type = 'bend';

          var cyPosX = tapStartPos.x;
          var cyPosY = tapStartPos.y;

          // Get which end point has been clicked (Source:0, Target:1, None:-1)
          var endPoint = getContainingEndPoint(cyPosX, cyPosY, edge);

          if (opts.handleReconnectEdge && (endPoint == 0 || endPoint == 1)) {
            edge.unselect();
            movedEndPoint = endPoint;
            detachedNode = (endPoint == 0) ? movedEdge.source() : movedEdge.target();

            var disconnectedEnd = (endPoint == 0) ? 'source' : 'target';
            var result = reconnectionUtilities.disconnectEdge(movedEdge, cy, event.renderedPosition, disconnectedEnd);

            dummyNode = result.dummyNode;
            movedEdge = result.edge;

            disableGestures();
          } else if (opts.handleAnchors) {
            movedAnchorIndex = undefined;
            createAnchorOnDrag = true;
          }
        });

        cy.on('drag', 'node', eDrag = function() {
          if (edgeToHighlight) {
            refreshDraws();
          }
        });

        cy.on('tapdrag', eTapDrag = function(event) {
          /**
           * if there is a selected edge set autounselectify false
           * fixes the node-editing problem where nodes would get
           * unselected after resize drag
           */
          if (cy.edges(':selected').length > 0) {
            cy.autounselectify(false);
          }
          var edge = movedEdge;

          if (movedEdge !== undefined && anchorPointUtilities.isIgnoredEdge(edge)) {
            return;
          }

          var type = anchorPointUtilities.getEdgeType(edge);

          if (createAnchorOnDrag && opts.enableCreateAnchorOnDrag && !anchorTouched && type !== 'none') {
            // remember state before creating anchor
            var weightStr = anchorPointUtilities.syntax[type]['weight'];
            var distanceStr = anchorPointUtilities.syntax[type]['distance'];

            moveAnchorParam = {
              edge: edge,
              type: type,
              weights: edge.data(weightStr) ? [].concat(edge.data(weightStr)) : [],
              distances: edge.data(distanceStr) ? [].concat(edge.data(distanceStr)) : []
            };

            // Capture full edge state for undo/redo
            anchorStateBefore = {
              edgeId: edge.id(),
              bendPointPositions: edge.data('bendPointPositions') ? JSON.parse(JSON.stringify(edge.data('bendPointPositions'))) : null,
              controlPointPositions: edge.data('controlPointPositions') ? JSON.parse(JSON.stringify(edge.data('controlPointPositions'))) : null,
              cyedgebendeditingDistances: edge.data('cyedgebendeditingDistances') ? [].concat(edge.data('cyedgebendeditingDistances')) : null,
              cyedgebendeditingWeights: edge.data('cyedgebendeditingWeights') ? [].concat(edge.data('cyedgebendeditingWeights')) : null,
              cyedgecontroleditingDistances: edge.data('cyedgecontroleditingDistances') ? [].concat(edge.data('cyedgecontroleditingDistances')) : null,
              cyedgecontroleditingWeights: edge.data('cyedgecontroleditingWeights') ? [].concat(edge.data('cyedgecontroleditingWeights')) : null
            };

            edge.unselect();

            // using tapstart position fixes bug on quick drags
            // ---
            // also modified addAnchorPoint to return the index because
            // getContainingShapeIndex failed to find the created anchor on quick drags
            movedAnchorIndex = anchorPointUtilities.addAnchorPoint(edge, tapStartPos);
            movedEdge = edge;
            createAnchorOnDrag = undefined;
            anchorCreatedByDrag = true;
            disableGestures();
          }

          // if the tapstart did not hit an edge and it did not hit an anchor
          if (!anchorTouched && (movedEdge === undefined ||
            (movedAnchorIndex === undefined && movedEndPoint === undefined))) {
            return;
          }

          var eventPos = event.position || event.cyPosition;

          // Update end point location (Source:0, Target:1)
          if (movedEndPoint != -1 && dummyNode) {
            dummyNode.position(eventPos);
          }
          // change location of anchor created by drag
          else if (movedAnchorIndex != undefined) {
            _moveAnchorOnDrag(edge, type, movedAnchorIndex, eventPos);
          }
          // change location of drag and dropped anchor
          else if (anchorTouched) {

            // the tapStartPos check is necessary when righ clicking anchor points
            // right clicking anchor points triggers MouseDown for Konva, but not tapstart for cy
            // when that happens tapStartPos is undefined
            if (anchorManager.touchedAnchorIndex === undefined && tapStartPos) {
              anchorManager.touchedAnchorIndex = getContainingShapeIndex(
                tapStartPos.x,
                tapStartPos.y,
                anchorManager.edge);
            }

            if (anchorManager.touchedAnchorIndex !== undefined) {
              _moveAnchorOnDrag(
                anchorManager.edge,
                anchorManager.edgeType,
                anchorManager.touchedAnchorIndex,
                eventPos
              );
            }
          }

          if (event.target && event.target[0] && event.target.isNode()) {
            nodeToAttach = event.target;
          }

        });

        cy.on('tapend', eTapEnd = function(event) {
          if (mouseOut) {
            canvas.getStage().fire('contentMouseup');
          }

          var edge = movedEdge || anchorManager.edge;

          if (edge !== undefined) {
            var index = anchorManager.touchedAnchorIndex;
            if (index != undefined) {
              // Check if source and target nodes are visible before accessing positions
              // This prevents issues when reshaping links with hidden source/target (e.g., inside collapsed parent)
              var sourceNode = edge.source();
              var targetNode = edge.target();
              if (!sourceNode || !targetNode || sourceNode.length === 0 || targetNode.length === 0 ||
                  !sourceNode.visible() || !targetNode.visible()) {
                // Skip anchor removal logic - cleanup will happen below
                index = undefined;
                anchorManager.touchedAnchorIndex = undefined;
              }
            }
            if (index != undefined) {
              var startX = edge.source().position('x');
              var startY = edge.source().position('y');
              var endX = edge.target().position('x');
              var endY = edge.target().position('y');

              var anchorList = anchorPointUtilities.getAnchorsAsArray(edge);
              var allAnchors = [startX, startY].concat(anchorList).concat([endX, endY]);

              var anchorIndex = index + 1;
              var preIndex = anchorIndex - 1;
              var posIndex = anchorIndex + 1;

              var anchor = { x: allAnchors[2 * anchorIndex], y: allAnchors[2 * anchorIndex + 1] };
              var preAnchorPoint = { x: allAnchors[2 * preIndex], y: allAnchors[2 * preIndex + 1] };
              var posAnchorPoint = { x: allAnchors[2 * posIndex], y: allAnchors[2 * posIndex + 1] };
              var nearToLine;

              if ((anchor.x === preAnchorPoint.x && anchor.y === preAnchorPoint.y) || (anchor.x === preAnchorPoint.x && anchor.y === preAnchorPoint.y)) {
                nearToLine = true;
              } else {
                var m1 = (preAnchorPoint.y - posAnchorPoint.y) / (preAnchorPoint.x - posAnchorPoint.x);
                var m2 = -1 / m1;

                var srcTgtPointsAndTangents = {
                  srcPoint: preAnchorPoint,
                  tgtPoint: posAnchorPoint,
                  m1: m1,
                  m2: m2
                };

                var currentIntersection = anchorPointUtilities.getIntersection(edge, anchor, srcTgtPointsAndTangents);
                var dist = Math.sqrt(Math.pow((anchor.x - currentIntersection.x), 2)
                  + Math.pow((anchor.y - currentIntersection.y), 2));

                // remove the bend point if segment edge becomes straight
                var type = anchorPointUtilities.getEdgeType(edge);
                if ((type === 'bend' && dist < options().bendRemovalSensitivity)) {
                  nearToLine = true;
                }
              }

              if (opts.enableRemoveAnchorMidOfNearLine && nearToLine) {
                anchorPointUtilities.removeAnchor(edge, index);
              }
            } else if (dummyNode != undefined && (movedEndPoint == 0 || movedEndPoint == 1)) {
              var newNode = detachedNode;
              var isValid = 'valid';
              var location = (movedEndPoint == 0) ? 'source' : 'target';

              // validate edge reconnection
              if (nodeToAttach) {
                var newSource = (movedEndPoint == 0) ? nodeToAttach : edge.source();
                var newTarget = (movedEndPoint == 1) ? nodeToAttach : edge.target();
                if (typeof validateEdge === 'function')
                  isValid = validateEdge(edge, newSource, newTarget);
                newNode = (isValid === 'valid') ? nodeToAttach : detachedNode;
              }

              var newSource = (movedEndPoint == 0) ? newNode : edge.source();
              var newTarget = (movedEndPoint == 1) ? newNode : edge.target();
              edge = reconnectionUtilities.connectEdge(edge, detachedNode, location);

              if (detachedNode.id() !== newNode.id()) {
                if (typeof opts.handleReconnectEdge === 'function') {
                  var reconnectedEdge = opts.handleReconnectEdge(newSource.id(), newTarget.id(), edge.data(), location);

                  if (reconnectedEdge) {
                    reconnectionUtilities.copyEdge(edge, reconnectedEdge);
                    anchorPointUtilities.initAnchorPoints(options().bendPositionsFunction,
                      options().controlPositionsFunction, [reconnectedEdge]);
                  }

                  if (reconnectedEdge && options().undoable) {
                    var params = { newEdge: reconnectedEdge, oldEdge: edge };
                    cy.undoRedo().do('removeReconnectedEdge', params);
                    edge = reconnectedEdge;
                  } else if (reconnectedEdge) {
                    cy.remove(edge);
                    edge = reconnectedEdge;
                  }
                } else {
                  var loc = (movedEndPoint == 0) ? { source: newNode.id() } : { target: newNode.id() };
                  var oldLoc = (movedEndPoint == 0) ? { source: detachedNode.id() } : { target: detachedNode.id() };

                  if (options().undoable && newNode.id() !== detachedNode.id()) {
                    var param = {
                      edge: edge,
                      location: loc,
                      oldLoc: oldLoc
                    };
                    var result = cy.undoRedo().do('reconnectEdge', param);
                    edge = result.edge;
                    //edge.select();
                  }
                }
              }

              // invalid edge reconnection callback
              if (isValid !== 'valid' && typeof actOnUnsuccessfulReconnection === 'function') {
                actOnUnsuccessfulReconnection(edge);
              }
              edge.select();
              cy.remove(dummyNode);
            }
          }
          var type = anchorPointUtilities.getEdgeType(edge);

          // to avoid errors
          if (type === 'none') {
            type = 'bend';
          }

          if (anchorManager.touchedAnchorIndex === undefined && !anchorCreatedByDrag) {
            moveAnchorParam = undefined;
          }

          var weightStr = anchorPointUtilities.syntax[type]['weight'];
          if (edge !== undefined && moveAnchorParam !== undefined &&
            (edge.data(weightStr) ? edge.data(weightStr).toString() : null) != moveAnchorParam.weights.toString()) {

            // anchor created from drag
            if (anchorCreatedByDrag) {
              edge.select();

              // stops the unbundled bezier edges from being unselected
              cy.autounselectify(true);
            }

            if (options().undoable) {
              cy.undoRedo().do('changeAnchorPoints', moveAnchorParam);
            }
          }

          movedAnchorIndex = undefined;
          movedEdge = undefined;
          moveAnchorParam = undefined;
          createAnchorOnDrag = undefined;
          movedEndPoint = undefined;
          dummyNode = undefined;
          detachedNode = undefined;
          nodeToAttach = undefined;
          tapStartPos = undefined;
          anchorCreatedByDrag = false;

          anchorManager.touchedAnchorIndex = undefined;

          resetGestures();
          refreshDraws();

          // Only emit moveend for edges that can have control/bend points
          // Skip BEZIER edges which never have anchors to prevent performance issues
          if (edge && edge.data) {
            var edgeType = edge.data('type');
            if (edgeType !== 'BEZIER' && anchorStateBefore) {
              cy.emit('edgeediting_moveend', [edge, anchorStateBefore]);
            }
          }
          // Clear the before state
          anchorStateBefore = null;
        });

        //Variables used for starting and ending the movement of anchor points with arrows
        var moveanchorparam;
        var firstAnchor;
        var edgeContainingFirstAnchor;
        var firstAnchorPointFound;
        cy.on('edgeediting.movestart', function(e, edges) {
          firstAnchorPointFound = false;
          if (edges[0] != undefined) {
            edges.forEach(function(edge) {
              if (anchorPointUtilities.getAnchorsAsArray(edge) != undefined && !firstAnchorPointFound) {
                firstAnchor = {
                  x: anchorPointUtilities.getAnchorsAsArray(edge)[0],
                  y: anchorPointUtilities.getAnchorsAsArray(edge)[1]
                };
                moveanchorparam = {
                  firstTime: true,
                  firstAnchorPosition: {
                    x: firstAnchor.x,
                    y: firstAnchor.y
                  },
                  edges: edges
                };
                edgeContainingFirstAnchor = edge;
                firstAnchorPointFound = true;
              }
            });
          }
        });

        cy.on('edgeediting.moveend', function(e, edges) {
          if (moveanchorparam != undefined) {
            var initialPos = moveanchorparam.firstAnchorPosition;
            var movedFirstAnchor = {
              x: anchorPointUtilities.getAnchorsAsArray(edgeContainingFirstAnchor)[0],
              y: anchorPointUtilities.getAnchorsAsArray(edgeContainingFirstAnchor)[1]
            };


            moveanchorparam.positionDiff = {
              x: -movedFirstAnchor.x + initialPos.x,
              y: -movedFirstAnchor.y + initialPos.y
            };

            delete moveanchorparam.firstAnchorPosition;

            if (options().undoable) {
              cy.undoRedo().do('moveAnchorPoints', moveanchorparam);
            }

            moveanchorparam = undefined;
          }
        });

        cy.on(opts.contexMenuShowEvent, eCxtTap = function(event) {
          var target = event.target || event.cyTarget;
          var targetIsEdge = false;

          try {
            targetIsEdge = target.isEdge();
          } catch (err) {
            // this is here just to suppress the error
          }

          var edge, type;
          if (targetIsEdge) {
            edge = target;
            type = anchorPointUtilities.getEdgeType(edge);
          } else {
            edge = anchorManager.edge;
            type = anchorManager.edgeType;
          }

          var cyPos = event.position || event.cyPosition;
          var selectedIndex = getContainingShapeIndex(cyPos.x, cyPos.y, edge);
          if (selectedIndex == -1) {
            anchorPointUtilities.currentCtxPos = cyPos;
          } else {
            anchorPointUtilities.currentAnchorIndex = selectedIndex;
          }
          anchorPointUtilities.currentCtxEdge = edge;
          if (!cy.contextMenus) {
            return;
          }

          var menus = cy.contextMenus('get'); // get context menus instance

          if (!edgeToHighlight || edgeToHighlight.id() != edge.id() || anchorPointUtilities.isIgnoredEdge(edge) ||
            edgeToHighlight !== edge) {
            if (opts.removeBendMenuItemTitle) {
              menus.hideMenuItem(removeBendPointCxtMenuId);
            }
            if (opts.addBendMenuItemTitle) {
              menus.hideMenuItem(addBendPointCxtMenuId);
            }
            if (opts.removeControlMenuItemTitle) {
              menus.hideMenuItem(removeControlPointCxtMenuId);
            }
            if (opts.addControlMenuItemTitle) {
              menus.hideMenuItem(addControlPointCxtMenuId);
            }
            return;
          }

          // not clicked on an anchor
          if (selectedIndex == -1) {
            if (opts.removeBendMenuItemTitle) {
              menus.hideMenuItem(removeBendPointCxtMenuId);
            }
            if (opts.removeControlMenuItemTitle) {
              menus.hideMenuItem(removeControlPointCxtMenuId);
            }
            if (type === 'control' && targetIsEdge) {
              if (opts.addControlMenuItemTitle) {
                menus.showMenuItem(addControlPointCxtMenuId);
              }
              if (opts.addBendMenuItemTitle) {
                menus.hideMenuItem(addBendPointCxtMenuId);
              }
            } else if (type === 'bend' && targetIsEdge) {
              if (opts.addBendMenuItemTitle) {
                menus.showMenuItem(addBendPointCxtMenuId);
              }
              if (opts.addControlMenuItemTitle) {
                menus.hideMenuItem(addControlPointCxtMenuId);
              }
            } else if (targetIsEdge) {
              if (opts.addBendMenuItemTitle) {
                menus.showMenuItem(addBendPointCxtMenuId);
              }
              if (opts.addControlMenuItemTitle) {
                menus.showMenuItem(addControlPointCxtMenuId);
              }
            } else {
              if (opts.addBendMenuItemTitle) {
                menus.hideMenuItem(addBendPointCxtMenuId);
              }
              if (opts.addControlMenuItemTitle) {
                menus.hideMenuItem(addControlPointCxtMenuId);
              }
            }
          }
          // clicked on an anchor
          else {
            if (opts.addBendMenuItemTitle) {
              menus.hideMenuItem(addBendPointCxtMenuId);
            }
            if (opts.addControlMenuItemTitle) {
              menus.hideMenuItem(addControlPointCxtMenuId);
            }
            if (type === 'control') {
              if (opts.removeControlMenuItemTitle) {
                menus.showMenuItem(removeControlPointCxtMenuId);
              }
              if (opts.removeBendMenuItemTitle) {
                menus.hideMenuItem(removeBendPointCxtMenuId);
              }
              if (opts.removeAllControlMenuItemTitle &&
                edge.hasClass('edgecontrolediting-hasmultiplecontrolpoints')) {
                menus.showMenuItem(removeAllControlPointCtxMenuId);
              }
            } else if (type === 'bend') {
              if (opts.removeBendMenuItemTitle) {
                menus.showMenuItem(removeBendPointCxtMenuId);
              }
              if (opts.removeControlMenuItemTitle) {
                menus.hideMenuItem(removeControlPointCxtMenuId);
              }
            } else {
              if (opts.removeBendMenuItemTitle) {
                menus.hideMenuItem(removeBendPointCxtMenuId);
              }
              if (opts.removeControlMenuItemTitle) {
                menus.hideMenuItem(removeControlPointCxtMenuId);
              }
              if (opts.removeAllControlMenuItemTitle) {
                menus.hideMenuItem(removeAllControlPointCtxMenuId);
              }
            }
          }
        });

        cy.on('cyedgeediting.changeAnchorPoints', 'edge', function() {
          var edge = this;
          cy.startBatch();
          cy.edges().unselect();

          // Listener defined in other extension
          // Might have compatibility issues after the unbundled bezier
          cy.trigger('bendPointMovement');

          cy.endBatch();
          refreshDraws();


        });
      }

      var selectedEdges;
      var anchorsMoving = false;

      // track arrow key presses, default false
      // event.keyCode normally returns number
      // but JS will convert to string anyway
      var keys = {
        '37': false,
        '38': false,
        '39': false,
        '40': false
      };

      function keyDown(e) {

        var shouldMove = typeof options().moveSelectedAnchorsOnKeyEvents === 'function'
          ? options().moveSelectedAnchorsOnKeyEvents() : options().moveSelectedAnchorsOnKeyEvents;

        if (!shouldMove) {
          return;
        }

        //Checks if the tagname is textarea or input
        var tn = document.activeElement.tagName;
        if (tn != 'TEXTAREA' && tn != 'INPUT') {
          switch (e.keyCode) {
            case 37:
            case 39:
            case 38:
            case 40: // Arrow keys
            case 32:
              e.preventDefault();
              break; // Space
            default:
              break; // do not block other keys
          }
          if (e.keyCode < '37' || e.keyCode > '40') {
            return;
          }
          keys[e.keyCode] = true;

          //Checks if only edges are selected (not any node) and if only 1 edge is selected
          //If the second checking is removed the anchors of multiple edges would move
          if (cy.edges(':selected').length != cy.elements(':selected').length || cy.edges(':selected').length != 1) {
            return;
          }
          if (!anchorsMoving) {
            selectedEdges = cy.edges(':selected');
            cy.trigger('edgeediting.movestart', [selectedEdges]);
            anchorsMoving = true;
          }
          var moveSpeed = 3;

          // doesn't make sense if alt and shift both pressed
          if (e.altKey && e.shiftKey) {
            return;
          } else if (e.altKey) {
            moveSpeed = 1;
          } else if (e.shiftKey) {
            moveSpeed = 10;
          }

          var upArrowCode = 38;
          var downArrowCode = 40;
          var leftArrowCode = 37;
          var rightArrowCode = 39;

          var dx = 0;
          var dy = 0;

          dx += keys[rightArrowCode] ? moveSpeed : 0;
          dx -= keys[leftArrowCode] ? moveSpeed : 0;
          dy += keys[downArrowCode] ? moveSpeed : 0;
          dy -= keys[upArrowCode] ? moveSpeed : 0;

          moveAnchorPoints({ x: dx, y: dy }, selectedEdges);
        }
      }

      function keyUp(e) {

        if (e.keyCode < '37' || e.keyCode > '40') {
          return;
        }
        e.preventDefault();
        keys[e.keyCode] = false;
        var shouldMove = typeof options().moveSelectedAnchorsOnKeyEvents === 'function'
          ? options().moveSelectedAnchorsOnKeyEvents() : options().moveSelectedAnchorsOnKeyEvents;

        if (!shouldMove) {
          return;
        }

        cy.trigger('edgeediting.moveend', [selectedEdges]);
        selectedEdges = undefined;
        anchorsMoving = false;

      }

      document.addEventListener('keydown', keyDown, true);
      document.addEventListener('keyup', keyUp, true);

      containerMetaData = data;
    },
    unbind: function() {
      cy.off('remove', 'node', eRemove)
        .off('add', 'node', eAdd)
        .off('style', 'edge.edgebendediting-hasbendpoints:selected, edge.edgecontrolediting-hascontrolpoints:selected', eStyle)
        .off('select', 'edge', eSelect)
        .off('unselect', 'edge', eUnselect)
        .off('mouseover', 'edge', eSelect)
        .off('mouseout', 'edge', eUnselect)
        .off('tapstart', eTapStart)
        .off('tapstart', 'edge', eTapStartOnEdge)
        .off('tapdrag', eTapDrag)
        .off('tapend', eTapEnd)
        .off('cxttap', eCxtTap)
        .off('drag', 'node', eDrag)
        .off('data', 'edge', eData);

      cy.unbind('zoom pan', eZoom);
    }
  };

  if (functions[fn]) {
    return functions[fn].apply(cy.container(), Array.prototype.slice.call(arguments, 1));
  } else if (typeof fn == 'object' || !fn) {
    return functions.init.apply(cy.container(), arguments);
  } else {
    console.error('No such function `' + fn + '` for cytoscape.js-edge-editing');
  }

  return this;
};
